// ============================================================================
// RemoteDevice_InfoPanel.tsx
// Panneau d'informations techniques côté device (bottom-sheet mobile).
//
// Affiche : plateforme, périphériques média, géolocalisation, codecs locaux.
//
// Refactorisations vs version originale (DeviceLocalInfoPanel.tsx) :
//   - InfoRow, InfoSection, InfoEmpty, CodecBadge → importés depuis SharedUI
//     (supprime ~60 lignes de redéfinitions locales)
//   - Prop renommée : activeStreamDetails → streamDetails (cohérence avec le
//     type ActiveStreamDetails et l'interface RemoteDevice_InfoPanelProps)
//   - highlight → accent sur InfoRow (alignement avec SharedUI)
// ============================================================================

import React, { useState } from 'react';

import { DeviceInfo, ActiveStreamDetails, GeolocationInfo, OrientationInfo, CodecInfo } from '../../../dataTypes/liveStreamManagment/types_media';
import { profileName } from '../../../dataTypes/liveStreamManagment/utils_codec';
import {
  NetworkQualityState, QUALITY_LABELS,
} from '../../../backendServices/liveStreamManagment/api_networkQuality';

import {
  InfoRow,
  Section,
  InfoEmpty,
  CodecBadge,
} from './ProductionControlRoom_SharedUI';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface RemoteDevice_InfoPanelProps {
  deviceInfo:    DeviceInfo;
  /**
   * Détails du stream actif.
   * Champs : .cameras, .mics, .activeVideo (ex .activeCamera),
   *           .activeMic (ex .activeMicrophone)
   */
  streamDetails: ActiveStreamDetails | null;
  geolocation:   GeolocationInfo;
  orientation:   OrientationInfo;
  codecInfo:     CodecInfo;
  /**
   * Qualité du réseau MONTANT, mesurée sur la PeerConnection qui porte la caméra.
   * À distinguer de `deviceInfo.connection*`, qui vient de l'API Network
   * Information du navigateur : celle-ci décrit le type de lien du téléphone
   * (« 4g », débit théorique descendant), pas ce que la publication obtient.
   */
  networkQuality?: NetworkQualityState;
  onClose:       () => void;
  onRequestGeo:  () => void;
}

// ─── Composant ────────────────────────────────────────────────────────────────

const RemoteDevice_InfoPanel: React.FC<RemoteDevice_InfoPanelProps> = ({
  deviceInfo,
  streamDetails,
  geolocation,
  codecInfo,
  networkQuality,
  onClose,
  onRequestGeo,
}) => {
  const [tab, setTab] = useState<'device' | 'media' | 'location' | 'codecs'>('device');

  const tabs: { id: typeof tab; label: string }[] = [
    { id: 'device',   label: '📱 Appareil' },
    { id: 'media',    label: '🎥 Médias'   },
    { id: 'location', label: '📍 Position' },
    { id: 'codecs',   label: '⚙️ Codecs'   },
  ];

  return (
    // Bottom-sheet mobile — style distinct du PanelOverlay desktop (centré)
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#0f1117', borderRadius: '20px 20px 0 0',
          width: '100%', maxWidth: 640, maxHeight: '88vh',
          overflow: 'hidden', display: 'flex', flexDirection: 'column',
          border: '1px solid rgba(255,255,255,0.08)', borderBottom: 'none',
          boxShadow: '0 -20px 60px rgba(0,0,0,0.6)',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Poignée */}
        <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 4px' }}>
          <div style={{ width: 40, height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.2)' }} />
        </div>

        {/* Header */}
        <div style={{ padding: '8px 20px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#fff' }}>Infos techniques</h2>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>
              {deviceInfo.platform} {deviceInfo.version} · {deviceInfo.browser}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.08)', border: 'none', color: '#fff',
              width: 32, height: 32, borderRadius: '50%', cursor: 'pointer', fontSize: 16,
            }}
          >×</button>
        </div>

        {/* Barre d'onglets */}
        <div style={{
          display: 'flex', margin: '0 16px 12px',
          background: 'rgba(255,255,255,0.06)', borderRadius: 12, padding: 3,
        }}>
          {tabs.map(({ id, label }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              style={{
                flex: 1, padding: '7px 4px', border: 'none', borderRadius: 10,
                cursor: 'pointer', fontSize: 11, fontWeight: 600,
                background: tab === id ? 'rgba(99,102,241,0.9)' : 'transparent',
                color:      tab === id ? '#fff' : 'rgba(255,255,255,0.45)',
                transition: 'all 0.15s',
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Contenu scrollable */}
        <div style={{
          flex: 1, overflowY: 'auto',
          padding: '0 16px 24px',
          display: 'flex', flexDirection: 'column', gap: 8,
        }}>

          {/* ── Appareil ── */}
          {tab === 'device' && (
            <>
              <Section title="Plateforme">
                <InfoRow label="OS"          value={`${deviceInfo.platform} ${deviceInfo.version}`} />
                <InfoRow label="Navigateur"  value={deviceInfo.browser} />
                <InfoRow label="RAM"         value={deviceInfo.memory ? `${deviceInfo.memory} Go` : 'N/A'} />
                <InfoRow label="CPU (cœurs)" value={deviceInfo.hardwareConcurrency?.toString() ?? '—'} />
              </Section>

              {/* Le lien du téléphone, tel que le navigateur le décrit. C'est une
                  indication de TYPE de connexion, pas une mesure de ce que la
                  publication obtient — d'où la section suivante. */}
              <Section title="Lien de l'appareil">
                <InfoRow
                  label="Type"
                  value={deviceInfo.connection?.toUpperCase() ?? '—'}
                  accent={deviceInfo.connection === '4g' ? 'green' : 'orange'}
                />
                <InfoRow
                  label="Débit descendant"
                  value={deviceInfo.connectionDownlink ? `${deviceInfo.connectionDownlink} Mb/s` : '—'}
                />
                <InfoRow
                  label="RTT"
                  value={deviceInfo.connectionRtt ? `${deviceInfo.connectionRtt} ms` : '—'}
                />
              </Section>

              {/* Ce que la publication obtient RÉELLEMENT — les chiffres qui
                  décident des barres affichées à côté du badge. */}
              <Section title="Qualité de la publication">
                {networkQuality?.level == null || !networkQuality.sample ? (
                  <InfoRow label="Mesure" value="en cours…" />
                ) : (() => {
                  const s     = networkQuality.sample;
                  const level = networkQuality.level;
                  const mbps  = (n: number) => `${(n / 1_000_000).toFixed(2)} Mb/s`;
                  // Vert au-dessus de Q3, orange en Q2, rouge en Q1 : la couleur
                  // reprend celle des barres, pour qu'on ne lise pas deux échelles.
                  const accent: 'green' | 'orange' | 'red' =
                    level >= 3 ? 'green' : level === 2 ? 'orange' : 'red';

                  return (
                    <>
                      <InfoRow
                        label="Niveau"
                        value={`Q${level} — ${QUALITY_LABELS[level].replace('Réseau ', '')}`}
                        accent={accent}
                      />
                      <InfoRow
                        label={s.bitrateSource === 'estimation' ? 'Débit disponible' : 'Débit disponible (estimé)'}
                        value={mbps(s.bitrate)}
                      />
                      {/* Le disponible vient du contrôle de congestion, l'émis du
                          compteur d'octets : les afficher côte à côte montre s'il
                          reste de la marge ou si l'on plafonne. */}
                      {s.sentBitrate !== undefined && (
                        <InfoRow label="Débit émis" value={mbps(s.sentBitrate)} />
                      )}
                      <InfoRow
                        label="Pertes"
                        value={s.packetsSent
                          ? `${(s.loss * 100).toFixed(2)} % (${s.packetsLost ?? 0} / ${s.packetsSent} paquets)`
                          : `${(s.loss * 100).toFixed(2)} %`}
                        accent={s.loss > 0.03 ? 'red' : s.loss > 0.01 ? 'orange' : 'green'}
                      />
                      <InfoRow
                        label="Aller-retour"
                        value={`${Math.round(s.rtt)} ms`}
                        accent={s.rtt > 200 ? 'red' : s.rtt > 100 ? 'orange' : 'green'}
                      />
                      {s.jitter !== undefined && (
                        <InfoRow label="Gigue" value={`${s.jitter.toFixed(1)} ms`} />
                      )}
                      {s.nackCount !== undefined && (
                        <InfoRow label="Retransmissions" value={`${s.nackCount} demandées`} />
                      )}
                      {s.frameWidth && s.frameHeight && (
                        <InfoRow
                          label="Émis"
                          value={`${s.frameWidth}×${s.frameHeight}${
                            s.framesPerSecond !== undefined ? ` · ${Math.round(s.framesPerSecond)} i/s` : ''}`}
                        />
                      )}
                      {/* Pourquoi l'encodeur a réduit, quand il l'a fait : sans
                          cette ligne, une image qui se dégrade passe pour une
                          panne de caméra. */}
                      {s.qualityLimitation && s.qualityLimitation !== 'none' && (
                        <InfoRow
                          label="Bridé par"
                          value={{
                            bandwidth: 'la bande passante',
                            cpu      : 'le processeur',
                            other    : 'une autre cause',
                          }[s.qualityLimitation] ?? s.qualityLimitation}
                          accent="orange"
                        />
                      )}
                      <InfoRow
                        label="Source du débit"
                        value={s.bitrateSource === 'estimation'
                          ? 'contrôle de congestion'
                          : 'octets émis (repli)'}
                      />
                    </>
                  );
                })()}
              </Section>
            </>
          )}

          {/* ── Médias ── */}
          {tab === 'media' && (
            <>
              {streamDetails ? (
                <>
                  <Section title={`Caméras (${streamDetails.cameras.length})`}>
                    {streamDetails.cameras.length > 0
                      ? streamDetails.cameras.map((c, i) => (
                          <InfoRow key={c.deviceId} label={`Cam ${i + 1}`} value={c.label} />
                        ))
                      : <InfoEmpty>Aucune caméra détectée</InfoEmpty>
                    }
                  </Section>

                  {streamDetails.activeVideo && (
                    <Section title="✅ Caméra active">
                      <InfoRow
                        label="Résolution"
                        value={`${streamDetails.activeVideo.width}×${streamDetails.activeVideo.height}`}
                        accent="green"
                      />
                      <InfoRow
                        label="FPS"
                        value={`${streamDetails.activeVideo.frameRate?.toFixed(1)} fps`}
                      />
                      <InfoRow
                        label="Orientation"
                        value={streamDetails.activeVideo.facingMode || '—'}
                      />
                    </Section>
                  )}

                  <Section title={`Micros (${streamDetails.mics.length})`}>
                    {streamDetails.mics.length > 0
                      ? streamDetails.mics.map((m, i) => (
                          <InfoRow key={m.deviceId} label={`Micro ${i + 1}`} value={m.label} />
                        ))
                      : <InfoEmpty>Aucun micro détecté</InfoEmpty>
                    }
                  </Section>

                  {streamDetails.activeMic && (
                    <Section title="✅ Micro actif">
                      <InfoRow label="Label"         value={streamDetails.activeMic.label} />
                      <InfoRow label="Taux (Hz)"     value={streamDetails.activeMic.sampleRate?.toString() ?? '—'} />
                      <InfoRow label="Canaux"        value={streamDetails.activeMic.channelCount?.toString() ?? '—'} />
                      <InfoRow
                        label="Echo cancel."
                        value={streamDetails.activeMic.echoCancellation ? 'Oui' : 'Non'}
                        accent={streamDetails.activeMic.echoCancellation ? 'green' : undefined}
                      />
                      <InfoRow
                        label="Noise suppress."
                        value={streamDetails.activeMic.noiseSuppression ? 'Oui' : 'Non'}
                        accent={streamDetails.activeMic.noiseSuppression ? 'green' : undefined}
                      />
                    </Section>
                  )}
                </>
              ) : (
                <Section title="Médias">
                  <InfoEmpty>Aucun stream actif</InfoEmpty>
                </Section>
              )}
            </>
          )}

          {/* ── Localisation ── */}
          {tab === 'location' && (
            <Section title="GPS">
              {geolocation.latitude != null ? (
                <>
                  <InfoRow label="Latitude"  value={`${geolocation.latitude.toFixed(6)}°`}  accent="green" />
                  <InfoRow label="Longitude" value={`${geolocation.longitude?.toFixed(6)}°`} accent="green" />
                  <InfoRow
                    label="Précision"
                    value={geolocation.accuracy ? `±${geolocation.accuracy.toFixed(0)} m` : '—'}
                  />
                </>
              ) : (
                <>
                  <InfoEmpty>
                    {geolocation.loading
                      ? 'Localisation en cours…'
                      : geolocation.error || 'Position non disponible'}
                  </InfoEmpty>
                  {!geolocation.loading && (
                    <button
                      onClick={onRequestGeo}
                      style={{
                        marginTop: 8, padding: '8px 16px',
                        background: 'rgba(99,102,241,0.8)', color: '#fff',
                        border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 12,
                      }}
                    >
                      Demander la position
                    </button>
                  )}
                </>
              )}
            </Section>
          )}

          {/* ── Codecs ── */}
          {tab === 'codecs' && (
            <>
              <Section title="Codecs vidéo supportés">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '4px 0' }}>
                  {codecInfo.available.video.length > 0
                    ? codecInfo.available.video.map(c => (
                        <CodecBadge
                          key={c}
                          label={c}
                          highlight={
                            c.toUpperCase().includes('H264') ? 'blue'   :
                            c.toUpperCase().includes('VP')   ? 'orange' : 'gray'
                          }
                        />
                      ))
                    : <InfoEmpty>Non disponible</InfoEmpty>
                  }
                </div>
              </Section>

              <Section title="Codecs audio supportés">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '4px 0' }}>
                  {codecInfo.available.audio.length > 0
                    ? codecInfo.available.audio.map(c => (
                        <CodecBadge
                          key={c}
                          label={c}
                          highlight={c.toUpperCase().includes('OPUS') ? 'green' : 'gray'}
                        />
                      ))
                    : <InfoEmpty>Non disponible</InfoEmpty>
                  }
                </div>
              </Section>

              {codecInfo.negotiatedWithRegie && (
                <Section title="✅ Négocié avec la régie">
                  <InfoRow
                    label="Codec vidéo"
                    value={codecInfo.negotiatedWithRegie.videoCodec || '—'}
                    accent="green"
                  />
                  <InfoRow
                    label="Profil H264"
                    value={
                      codecInfo.negotiatedWithRegie.videoProfile
                        ? `${profileName(codecInfo.negotiatedWithRegie.videoProfile)} (${codecInfo.negotiatedWithRegie.videoProfile})`
                        : '—'
                    }
                    accent={
                      codecInfo.negotiatedWithRegie.videoProfile?.toLowerCase().startsWith('42e0')
                        ? 'green' : 'orange'
                    }
                  />
                  <InfoRow
                    label="Audio"
                    value={codecInfo.negotiatedWithRegie.audioCodec || '—'}
                  />
                </Section>
              )}
            </>
          )}

        </div>
      </div>
    </div>
  );
};

export default RemoteDevice_InfoPanel;
